import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CommentType,
  NameIdType,
  PriorityEnum,
} from "../../assets/utils/enums/project";
import { FinanceRow, TaskRow } from "../../assets/utils/table-types";
import { ModalWindowType } from "../../components/ui/modal-window";
import getData, { FetchError } from "../../api/project-request";
import {
  API_URL_COMMENT,
  API_URL_SELLING,
} from "../../constants";
import { formatDate } from "../../assets/utils/date-formatter";
import toast from "react-hot-toast";
import CustomToast from "../../components/ui/custom-toast";
import { ToastTypeEnum } from "../../assets/utils/enums/toast";
import { authFetch } from "../../api/authFetch";
import AppLayout from "../../components/layout/app/layout";
import Headline from "../../components/ui/headline";
import { HeadlineTypeEnum } from "../../assets/utils/enums/headline";
import { ThemeTypeEnum } from "../../assets/utils/validation/types";
import { BodyDiv } from "../../components/components-style";
import { Button } from "../../components/ui/button";
import { COLORS } from "../../assets/utils/enums/colors";
import {
  BoldTextBody,
  LittleHeadline,
  LittleSubHeadline,
  LittleText,
  MediumText,
  NotificationText,
} from "../../components/typography";
import folderIcon from "../../assets/images/icons/folder-icon.svg";
import { ButtonSpan, ItemMainBlock } from "../project/details";
import { LinkText } from "../../components/ui/error-text";
import { ActionsEnum } from "../../assets/utils/enums/settings";
import DataTable from "../../components/ui/table";
import { tableColumnType, TableTypeEnum } from "../../assets/utils/enums/table";
import { FinanceTypeTable } from "../../assets/utils/enums/finance";
import {
  SellingStage,
  sellingStagesObj,
} from "../../assets/utils/enums/selling";
import CommentListDetails from "../../components/ui/comment/comment-details";
import CommentAdd from "../../components/ui/comment/comment-add";
import ErrorPage from "../../components/layout/app/error-page";
import { ErrorVariantEnum } from "../../assets/utils/enums/error-page";
import handleErrorData from "../../api/error-handler";

export default function DetailsSellingPage() {
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const [name, setName] = useState<string>("");
  const [client, setClient] = useState<string>("");
  const [deadline, setDeadline] = useState<string>("");
  const [clientFullName, setClientFullName] = useState<string>("");
  const [responsibleContact, setResponsibleContact] = useState<string>("");
  const [responsibleContactFullName, setResponsibleContactFullName] =
    useState<string>("");
  const [budget, setBudget] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [stage, setStage] = useState<string>(SellingStage.ON_HOLD);
  const [project, setProject] = useState<NameIdType>({ id: "", name: "" });
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [expenses, setExpenses] = useState<FinanceRow[]>([]);
  const [incomes, setIncomes] = useState<FinanceRow[]>([]);
  const [comments, setComments] = useState<CommentType[]>([]);
  const [isChangedComments, setIsChangedComment] = useState<boolean>(false);
  const [errorStatus, setErrorStatus] = useState<ErrorVariantEnum | null>(null);

  const [modalWindowSettings, setModalWindowSettings] =
    useState<ModalWindowType>({
      isOpen: false,
      onConfirm: () => { },
      onCancel: () => { },
    });

  const getComments = async () => {
    setIsLoading(true);
    try {
      const data = await getData(
        `${API_URL_COMMENT}/get-comments-by-selling?sellingId=${id}`
      );
      if (Array.isArray(data)) {
        setComments(data);
      } else {
        setComments([]);
      }
    } catch (error: any) {
      console.log(error.message);
      setComments([]);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  };

  const loadData = async () => {
    try {
      const data = await getData(`${API_URL_SELLING}/get-details?id=${id}`);
      if (!data || Object.keys(data).length === 0) {
        setErrorStatus(ErrorVariantEnum.FORBIDDEN);
        return false;
      }

      setName(data.name);
      setClient(data.clientEmail || "");
      setClientFullName(data.clientFullName || "");
      setCompany(data.companyName || "—");
      setDescription(data.description || "");
      setBudget(data.formattedBudget?.toString() || "");
      setDeadline(formatDate(data.deadline) || "");
      setResponsibleContact(data.responsibleEmail || "");
      setProject({ id: data.projectId, name: data.projectName || "—" });
      setResponsibleContactFullName(data.responsibleFullName || "");
      setStage((data.stageName as SellingStage) || SellingStage.ON_HOLD);

      return true;
    } catch (e) {
      const error = e as FetchError;

      handleErrorData(error, setErrorStatus);
      return false;
    }
  };

  const getTasks = async () => {
    setIsLoading(true);
    try {
      const data = await getData(`${API_URL_SELLING}/get-tasks?sellingId=${id}`);

      if (Array.isArray(data)) {
        setTasks(
          data.map(
            (item): TaskRow => ({
              isChecked: false,
              id: item.id,
              name: item.name || "",
              description: item.description || "",
              deadline: item.deadline || "",
              status: item.status,
              priorityName: item.priorityName || PriorityEnum.LOW,
              createdAt: item.createdAt || Date.now(),
              createdBy: item.responsibleEmail || "",
              usersEmails: item.usersEmails || [""],
              project: item.project || "",
              projectId: item.projectId,
              selling: item.selling || "",
              sellingId: item.sellingId,
            })
          )
        );
      } else {
        setTasks([]);
      }
    } catch (error: any) {
      console.log(error.message);
      setTasks([]);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  };

  const getExpenses = async () => {
    setIsLoading(true);
    try {
      const data = await getData(
        `${API_URL_SELLING}/get-expenses?sellingId=${id}`
      );
      if (Array.isArray(data)) {
        setExpenses(data);
      } else {
        setExpenses([]);
      }
    } catch (error: any) {
      console.log(error.message);
      setExpenses([]);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  };

  const getIncomes = async () => {
    setIsLoading(true);
    try {
      const data = await getData(
        `${API_URL_SELLING}/get-incomes?sellingId=${id}`
      );
      if (Array.isArray(data)) {
        setIncomes(data);
      } else {
        setIncomes([]);
      }
    } catch (error: any) {
      console.log(error.message);
      setIncomes([]);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      const hasAccess = await loadData();

      if (!hasAccess) {
        setIsLoading(false);
        return;
      }

      await Promise.all([
        getTasks(),
        getExpenses(),
        getIncomes(),
        getComments(),
      ]);

      setIsLoading(false);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      getComments();
      setIsChangedComment(false);
      setIsLoading(false);
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChangedComments]);


  async function deleteFunc() {
    const response = await authFetch(`${API_URL_SELLING}/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (response.ok) {
      toast((t) => (
        <CustomToast
          t={t}
          type={ToastTypeEnum.INFO}
          text={"Видалення успішне."}
        />
      ));
    }

    setModalWindowSettings((prev) => ({ ...prev, isOpen: false }));
    navigate("/selling/main");
  }

  const handleCreateProject = () => {
    navigate("/projects/add", {
      state: {
        name,
        description,
        budget,
        responsibleContact,
        client,
        company,
        sellingId: Number.parseInt(id!)
      },
    });
  };

  if (!isLoading && errorStatus) {
    return (
      <ErrorPage
        variant={errorStatus}
        onAction={() => navigate(-1)}
        additionalBtn="Дo Продажів"
        onActionAddBtn={() => navigate("/selling/main")}
      />
    );
  }

  return (
    <AppLayout
      className="d-flex flex-column align-items-center gap-2"
      blockType="details-project"
      loaderState={isLoading}
      modalWindow={modalWindowSettings}
    >
      <Headline
        headline={HeadlineTypeEnum.SELLING}
        theme={ThemeTypeEnum.LIGHT}
        btn_text="Повернутися до Продажів"
        btn_link="/selling/main"
        isEdit={false}
      />
      <div className="w-100 d-flex gap-3">
        <BodyDiv
          data-class="detail-main-block"
          className="justify-content-between w-60"
        >
          <span className="d-flex align-items-center gap-3">
            <Button
              bgcolor={COLORS.main2}
              txtcolor={COLORS.white}
              brcolor={"transparent"}
              width="auto"
              className="center-btn"
              brradius="12px"
              type="large"
              onClick={() => { }}
            >
              <img src={folderIcon} alt="folder-icon" />
            </Button>
            <span className="d-flex flex-column ">
              <LittleHeadline>{name}</LittleHeadline>
              <span className="d-flex align-items-center gap-2">
                <NotificationText>Дедлайн:</NotificationText>
                <BoldTextBody>{deadline}</BoldTextBody>
              </span>
            </span>
          </span>
          <span className="d-flex justify-content-between gap-4">
            <ItemMainBlock>
              <NotificationText>Компанія</NotificationText>
              <LittleSubHeadline>{company}</LittleSubHeadline>
            </ItemMainBlock>
            <ItemMainBlock>
              <NotificationText>Представник компанії</NotificationText>
              <LinkText mainText={clientFullName} addText={client} />
            </ItemMainBlock>
          </span>
          <ItemMainBlock className="w-100 mw-100">
            <NotificationText>Опис продажі</NotificationText>
            <LittleSubHeadline>
              {description || "Відсутній опис"}
            </LittleSubHeadline>
          </ItemMainBlock>

          <ButtonSpan>
            <Button
              bgcolor={COLORS.secondary1}
              txtcolor={COLORS["light-gray"]}
              brcolor={"transparent"}
              width="50%"
              className="center-btn"
              brradius="12px"
              type="small"
              onClick={() => {
                navigate(`/selling/update/${id}`);
              }}
            >
              <LittleText>Редагувати дані</LittleText>
            </Button>
            <Button
              bgcolor={COLORS.warning}
              txtcolor={COLORS["light-gray"]}
              brcolor={"transparent"}
              width="50%"
              className="center-btn"
              brradius="12px"
              type="small"
              onClick={() => {
                setModalWindowSettings({
                  isOpen: true,
                  onConfirm: deleteFunc,
                  onCancel: () =>
                    setModalWindowSettings((prev) => ({
                      ...prev,
                      isOpen: false,
                    })),
                  action: ActionsEnum.DELETE,
                });
              }}
            >
              <LittleText>Видалити</LittleText>
            </Button>
          </ButtonSpan>
        </BodyDiv>
        <div data-class="detail-add-block" className="w-40">
          <BodyDiv className="h-100 justify-content-between">
            <ItemMainBlock>
              <NotificationText>Відповідальний контакт</NotificationText>
              <LinkText
                mainText={responsibleContactFullName}
                addText={responsibleContact}
              // attribute="column"
              />
            </ItemMainBlock>
            <ItemMainBlock>
              <NotificationText>Бюджет</NotificationText>
              <LittleSubHeadline>{budget}</LittleSubHeadline>
            </ItemMainBlock>
            <ItemMainBlock className="max-w-100">
              <NotificationText>Проєкт</NotificationText>
              {project.name === "—" ? (
                <LittleSubHeadline>{project.name}</LittleSubHeadline>
              ) : (
                <a href={`/projects/details/${project.id}`}>
                  <LittleSubHeadline>{project.name}</LittleSubHeadline>
                </a>
              )}
            </ItemMainBlock>

            <ItemMainBlock className="w-100">
              <NotificationText>Етап</NotificationText>
              <Button
                bgcolor={sellingStagesObj[stage as SellingStage].bgColor}
                txtcolor={sellingStagesObj[stage as SellingStage].txtColor}
                brcolor={sellingStagesObj[stage as SellingStage].txtColor}
                width="100%"
                className="center-btn border-2"
                brradius="24px"
                type="large"
                onClick={() => { }}
              >
                <LittleText>{stage}</LittleText>
              </Button>
            </ItemMainBlock>
          </BodyDiv>
        </div>
      </div>
      {stage === SellingStage.CLOSED_DEAL && (
        <BodyDiv
          data-class="add-comment"
          className="d-flex w-100 gap-3 justify-content-between align-items-center"
        >
          <MediumText className="fw-normal d-flex flex-wrap w-auto row-gap-1">
            Продаж перенесено до етапу
            <span className="fw-medium px-2">{SellingStage.CLOSED_DEAL}.</span>
            Створити проєкт для цього продажу?
          </MediumText>
          <Button
            bgcolor={COLORS.secondary1}
            txtcolor={COLORS.white}
            brcolor={COLORS.secondary1}
            width="auto"
            brradius="24px"
            type="small"
            onClick={handleCreateProject}
          >
            <NotificationText>Створити проєкт</NotificationText>
          </Button>
        </BodyDiv>
      )}

      <CommentAdd type="selling" objectId={Number.parseInt(id!)} setIsLoading={setIsLoading} setIsChangedComment={setIsChangedComment} />
      <CommentListDetails comments={comments} setIsChangedComment={setIsChangedComment} setModalWindowSettings={setModalWindowSettings} setIsLoading={setIsLoading} />
      <DataTable
        table={tableColumnType[TableTypeEnum.TASKS]}
        data={tasks}
        isPage={"selling" as TableTypeEnum}
      />
      <DataTable
        table={tableColumnType[TableTypeEnum.FINANCE]}
        data={expenses}
        isPage={"selling" as TableTypeEnum}
        isFinanceTitle={FinanceTypeTable.EXPENSE}
      />
      <DataTable
        table={tableColumnType[TableTypeEnum.FINANCE]}
        data={incomes}
        isPage={"selling" as TableTypeEnum}
        isFinanceTitle={FinanceTypeTable.INCOME}
      />
    </AppLayout>
  );
}
